<template>
  <div class='cloud-asset-row'>
    <div class='cloud-asset-row__select'>
      <div class='check-box'>
        <custom-checkbox
          color="cold-marine"
          :value="value"
          size="s"
          @input="value = !value"
        />
      </div>
    </div>
    <div class='cloud-asset-row__content'>
      <div class="main-info" @click="isOpen = !isOpen">
        <div class='cloud-asset-row__actions' :class="{ successful: !isVulnerable }">
          <v-menu
            bottom
            right
            offset-x
            :close-on-content-click='true'
            :nudge-width="100"
            rounded
            :disabled='!cloudActions.length'
          >
            <template v-slot:activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
                id='more'
                class='material-icons-outlined more-btn'
              >
                more_vert
              </div>
            </template>

            <v-list>
              <v-list-item
                v-for="(action, i) in cloudActions"
                :key="i"
                link
                @click='initiateActionFromMenu(action.value)'
              >
                <v-list-item-title>{{ action.title }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <span
            class="material-icons-round logo"
            :class="{ successful: !isVulnerable }"
            :style="`color: ${vulnerabilityIcon.color}`"
            v-if='!isVulnerable'
          >
        {{ vulnerabilityIcon.iconName }}
      </span>
          <custom-tooltip popper-class="light-tooltip" v-else>
            <div slot='buttonContent'>
          <span class="material-icons-round logo" :style="`color: ${vulnerabilityIcon.color}`"  @click='goToIssues'>
            {{ vulnerabilityIcon.iconName }}
          </span>
            </div>
            <div slot="content">
              <slot name="tooltip">
                {{ warningTooltip }}
              </slot>
            </div>
          </custom-tooltip>
        </div>
        <div class="url">
          <div class="url__data">
            <custom-tooltip class="url__data__tooltip" placement="right" popper-class="light-tooltip">
              <div slot='buttonContent'>
                {{ formatUrlText }}
              </div>
              <div slot="content">
                <slot name="tooltip">
                  {{ formatUrlText }}
                </slot>
              </div>
            </custom-tooltip>
          </div>
          <div class='url__logo'>
            <img  v-if="logo" :src="logo" />
          </div>
          <div class="url__open">
            <custom-tooltip placement="right" popper-class="light-tooltip">
              <div slot='buttonContent'>
                <v-btn :disabled="!isValidURl" class="open-cloud-btn" :href="formatUrlLink" target="_blank" :ripple="false" @click.stop="">
                  <v-icon>open_in_new</v-icon>
                </v-btn>
              </div>
              <div slot="content">
                <slot name="tooltip">
                  Open URI
                </slot>
              </div>
            </custom-tooltip>
          </div>
        </div>
        <div class='ports'>
          <v-chip-group
            :show-arrows='true'
          >
            <div v-for='(port, indexPort) in sortedPorts' :key="indexPort">
              <custom-tooltip popper-class="light-tooltip"  v-if='getService(port)'>
                <div slot='buttonContent'>
                  <v-chip
                    class='port'
                    :class="[(isPortVulnerable(port))? 'vulnerable' : '', isClickablePort(port)? 'port__clickable':'']"
                    @click.stop='portClickHandler(port)'
                  >
                    {{ port }}
                  </v-chip>
                </div>
                <div slot='content'>
                  <slot name="tooltip">
                    {{getService(port)}}
                  </slot>
                </div>
              </custom-tooltip>
              <div v-else>
                <v-chip
                  class='port'
                  :class="[(isPortVulnerable(port))? 'vulnerable' : '', isClickablePort(port)? 'port__clickable':'']"
                  @click.stop='portClickHandler(port)'
                >
                  {{ port }}
                </v-chip>
              </div>
            </div>
          </v-chip-group>
        </div>
        <div class='cloud-account'>
          {{ getCloudAccount }}
        </div>
        <div class='status'>
          <div class='label'>
            <div :class="['label', { colorBoolean }]">
              {{ item.status }}
            </div>
          </div>
        </div>
        <div class='last-scanned-date'>
          {{ getLastScannedDate }}
        </div>
        <div class='right-section'>
          <div class='add-test-card' v-if='isCloudExposed'>
            <custom-tooltip placement="right" popper-class="light-tooltip" v-if="!isOpen">
              <div slot='buttonContent'>
                <v-btn class="add-test-btn" @click.stop="openAddTestPopup"  :ripple="false" :disabled='addTestCardDisabled || !isValidURl'>
                  <v-icon class="add-test-icon-no-txt">add</v-icon>
                </v-btn>
              </div>
              <div slot="content">
                <slot name="tooltip">
                  {{ toolTipText }}
                </slot>
              </div>
            </custom-tooltip>
          </div>
          <div class='arrow-expand-section' v-if='displayOpenedRow'>
            <v-icon class="expand-icon" :class="{ expand: isOpen }">expand_more</v-icon>
          </div>
        </div>
      </div>
      <div class="test-container" v-if="isOpen && isCloudExposed">
        <test-card
          v-for="(testCard, key) in testCardsForDisplay"
          :test-card="testCard"
          :item="testCard.url"
          :key="key"
          @displayError='displayTestCardError'
        />
        <div v-if='isCloudExposed && !addTestCardDisabled && isValidURl' class="add-test-area" v-ripple="{ class: `blue--text` }" @click="openAddTestPopup">
          <v-icon class="add-test-icon">add</v-icon>
          <span class="add-test-btn-txt">{{ $t('configuration.add.test') }}</span>
        </div>
        <div class='test-card-display-toggle' v-if='(testCardsForCloudAsset || []).length > amountOfTestCardInRow'>
          <span text class='toggle-test-card-btn' @click.stop="displayAllTestCards = !displayAllTestCards">
            {{ displayTestCardsButtonLabel }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { CONFIGURATION } from '@/constants/configuration';
import awsAuthenticatedIcon from '@/assets/aws_authenticated_icon.svg';
import awsIcon from '@/assets/aws.svg';
import gcpAuthenticatedIcon from '@/assets/gcp_authenticated_icon.svg';
import gcpIcon from '@/assets/gcp.svg';
import azureAuthenticatedIcon from '@/assets/azure_authenticated_icon.svg';
import azureIcon from '@/assets/azure.svg';
// import moment from 'moment';
import CustomCheckbox from '@/components/common/CustomCheckbox.vue';
import TestCard from '@/components/configuration/TestCard.vue';
import moment from 'moment';
import { views } from '@/router/constants';
import CustomTooltip from '@/components/common/CustomTooltip.vue';
import { mapGetters } from 'vuex';
import {rules} from '@/utils/validationUtils';

export default {
  name: 'CloudAssetRow',
  props: {
    addTestCardDisabled: {
      type: Boolean,
      default: false,
      required: false,
    },
    amountOfTestCardInRow: {
      type: Number,
      default: 5,
      required: false,
    },
    item: {
      type: Object,
      required: true,
    },
    selectedItems: {
      type: Array,
      required: true,
      default: () => [],
    },
    excludedItems: {
      type: Array,
      required: true,
      default: () => [],
    },
    isAllSelected: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  components: {
    TestCard,
    CustomCheckbox,
    CustomTooltip,
  },
  data() {
    return {
      displayAllTestCards: false,
      isOpen: false,
      addTestPopup: false,
      showPopup: false,
    };
  },
  methods: {
    async goToIssues() {
      await this.$router.push({ name: views.ISSUES, params: { tld: this.item.url, specificSearch: 'true' }});
    },
    async displayTestCardError(errorMessage) {
      await this.$emit('displayTestCardError', errorMessage);
    },
    async openAddTestPopup() {
      if (this.isCloudExposed) {
        await this.$emit('openAddTestPopup', {
          item: this.item,
        });
      }
    },
    async initiateActionFromMenu(action) {
      await this.$emit('initiateAction', { action, item: this.item });
    },
    isClickablePort(port) {
      const ports = ['80', '81', '443', '2000', '8000', '8008', '8080', '8443', '8888', '10000', '49152', '9200'];
      return ports.includes(port.toString()) && this.isPortVulnerable(port);
    },
    portClickHandler(port) {
      if (this.isClickablePort(port)) {
        if (['80'].includes(port.toString())) {
          window.open(`http://${this.item.url}`, '_blank');
        } else if (['443'].includes(port.toString())) {
          window.open(`https://${this.item.url}`, '_blank');
        } else if (['8000', '8080', '8888', '10000', '2000', '81'].includes(port.toString())) {
          window.open(`https://${this.item.url}:${port}`, '_blank');
        } else if (['8008', '8443', '49152'].includes(port.toString())) {
          window.open(`https://${this.item.url}:${port}`, '_blank');
        } else if (['9200'].includes(port.toString())) {
          window.open(`https://${this.item.url}:${port}/_aliases?pretty=true`, '_blank');
        }
      }
    },
    isPortVulnerable(port) {
      return this.vulnerablePorts.includes(port);
    },
    getService(port) {
      return this.portsToScoreMap[port]?.service || null;
    },
  },
  computed: {
    ...mapGetters([
      'portsToScoreMap',
      'testCardsSubdomains',
    ]),
    urlRules() {
      return {
        ...rules,
      };
    },
    rules() {
      return [this.urlRules.subdomain];
    },
    isValidURl() {
      const validationForCurrentRef = this.rules.map(rule => rule(this.getData));
      const booleanValue = validationForCurrentRef.every(e => ((typeof (e) === 'boolean') && (e === true)));
      return booleanValue;
    },
    cloudActions() {
      let actions = Object.keys(CONFIGURATION.CLOUD_ACTIONS_TYPES).map(item => ({ value: item, title: this.$t(`cloud.assets.action.${item}`) }));
      if (this.item.suppress) {
        actions = actions.filter(action => action.value === CONFIGURATION.CLOUD_ACTIONS_TYPES.unsuppressed);
      } else {
        actions = actions.filter(action => action.value === CONFIGURATION.CLOUD_ACTIONS_TYPES.suppress);
      }
      return actions;
    },
    getLastScannedDate() {
      if (this.item?.lastScannedDate && this.item?.lastScannedDate !== '') {
        return moment(this.item.lastScannedDate).format('DD MMM YYYY');
      }
      return '';
    },
    getCloudAccount() {
      return this.item?.tld || '';
    },
    toolTipText() {
      return this.addTestCardDisabled ? this.$t('license.disable.above.3.scans') : this.$t('configuration.add.test');
    },
    warningTooltip() {
      if (this.vulnerabilityIcon.severity === 'critical') {
        return this.$t('configuration.critical.vulnerable.asset.tooltip');
      }
      if (this.vulnerabilityIcon.severity === 'medium') {
        return this.$t('configuration.medium.vulnerable.asset.tooltip');
      }
      return this.$t('configuration.low.vulnerable.asset.tooltip');
    },
    isVulnerable() {
      return Object.keys(this.item.vulnerabilityDetails).some(key => this.item.vulnerabilityDetails[key] > 0);
    },
    vulnerabilityIcon() {
      const vulnerabilityKeys = Object.keys(this.item.vulnerabilityDetails);
      if ((vulnerabilityKeys.includes('critical') && this.item.vulnerabilityDetails.critical > 0) || (vulnerabilityKeys.includes('high') && this.item.vulnerabilityDetails.high > 0)) {
        return { iconName: 'warning', color: '#E0255E', severity: 'critical' };
      }
      if (vulnerabilityKeys.includes('medium') && this.item.vulnerabilityDetails.medium > 0) {
        return { iconName: 'warning', color: '#e0ad42', severity: 'medium' };
      }
      if (vulnerabilityKeys.includes('low') && this.item.vulnerabilityDetails.low > 0) {
        return { iconName: 'warning', color: '#2459D5', severity: 'low' };
      }
      return { iconName: 'check_circle', color: '#2baf69' };
    },
    displayTestCardsButtonLabel() {
      return this.displayAllTestCards ? this.$t('hide.test.cards') : this.$t('show.all.test.cards');
    },
    testCardsForCloudAsset() {
      const allSubdomainsTestCards = { ...this.testCardsSubdomains };
      if (Object.keys(allSubdomainsTestCards).includes(this.item.url)) {
        const testCardsFilterByTLD = allSubdomainsTestCards[this.item.url].filter(testCard => testCard.tld === this.item.tld);
        return testCardsFilterByTLD.sort((a, b) => (moment(a.createdDate).isAfter(moment(b.createdDate)) ? -1 : 1));
      }
      return [];
    },
    testCardsForDisplay() {
      const maxPossibleTestCards = this.testCardsForCloudAsset?.length < this.amountOfTestCardInRow ? this.testCardsForCloudAsset?.length : this.amountOfTestCardInRow;
      return this.displayAllTestCards ? this.testCardsForCloudAsset : this.testCardsForCloudAsset.slice(0, maxPossibleTestCards);
    },
    displayOpenedRow() {
      return this.isCloudExposed || this.testCardsForCloudAsset?.length;
    },
    isCloudExposed() {
      return this.item.status === CONFIGURATION.CLOUD_STATUS.exposed;
    },
    colorBoolean() {
      return !(this.item.status === CONFIGURATION.CLOUD_STATUS.private);
    },
    vulnerablePorts() {
      return this.item.vulnerablePorts || [];
    },
    sortedPorts() {
      return [...this.filteredPorts]
        // eslint-disable-next-line no-nested-ternary
        .sort((a, b) => (this.vulnerablePorts.includes(a) ? -1 : this.vulnerablePorts.includes(b) ? 1 : parseInt(a, 10) - parseInt(b, 10)));
    },
    filteredPorts() {
      return Array.from(new Set(this.ports
        .filter(port => !Number.isNaN(parseInt(port, 10)) && parseInt(port, 10) > 1 && parseInt(port, 10) < 65536)));
    },
    ports() {
      return this.item.ports || [];
    },
    value: {
      // eslint-disable-next-line no-unused-vars
      set(value) {
        this.$emit('checked', this.item);
      },
      get() {
        return this.selectedItems.includes(`${this.item.url}_${this.item.tld}`) ||
          (this.isAllSelected && this.item?.selected !== undefined && this.item.selected && !this.excludedItems.includes(`${this.item.url}_${this.item.tld}`)) ||
          (this.isAllSelected && this.item?.selected === undefined && !this.excludedItems.includes(`${this.item.url}_${this.item.tld}`));
      },
    },
    getData() {
      return this.item.url ? this.item.url.replaceAll('__', '.') : this.item.cloud.replaceAll('__', '.');
    },
    formatUrlLink() {
      const url = this.getData;
      return url.startsWith('http') ? url : `http://${url}`;
    },
    formatUrlText() {
      const url = this.getData;
      if (this.item.authenticated) {
        return url;
      }
      return url.startsWith('http') ? url : `http://${url}`;
    },
    logo() {
      if (this.item.source) {
        if (this.item.source === CONFIGURATION.SOURCE_TYPE.aws) {
          if (this.item.authenticated) {
            return awsAuthenticatedIcon;
          }
          return awsIcon;
        }
        if (this.item.source === CONFIGURATION.SOURCE_TYPE.gcp) {
          if (this.item.authenticated) {
            return gcpAuthenticatedIcon;
          }
          return gcpIcon;
        }
        if (this.item.source === CONFIGURATION.SOURCE_TYPE.azure) {
          if (this.item.authenticated) {
            return azureAuthenticatedIcon;
          }
          return azureIcon;
        }
      }
      return null;
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/styles/design-system';

// Vuetify list overrides - use doubled selectors for specificity
.theme--light.v-list.v-list {
  width: rem(135);
}

.v-list-item.v-list-item {
  min-height: rem(30);
  padding-left: $space-4;
  width: fit-content;
}

.list-group-title {
  padding-left: $space-4;
}

.list-sub-group-item.list-sub-group-item {
  padding-left: $space-4;
}

:deep(.v-list-item__title) {
  font-family: $default-application-font-medium;
  font-size: $font-size-base;
}
</style>

<style lang='scss'>
@import '~@/assets/styles/design-system';

.cloud-asset-row {
  .v-slide-group__wrapper {
    padding-left: 0;
    background-color: transparent;
  }
}
</style>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.cloud-asset-row {
  min-height: rem(58);
  border-bottom: 1px solid var(--border-primary);
  display: flex;
  padding: $space-1 0;
  align-items: center;

  &__select {
    margin-top: -$space-1;
  }

  &__open {
    padding-top: $space-0-5;
    z-index: 4;
  }

  &__actions {
    display: flex;
    align-items: center;
    margin-right: $space-1;

    .more-btn {
      margin-top: -1px;
      opacity: 0;
      color: var(--text-muted);

      &:hover {
        cursor: pointer;
        opacity: 100;
      }
    }

    .material-icons-round {
      margin-right: $space-1;
      margin-top: -1px;

      &.successful {
        &:hover {
          cursor: auto;
        }
      }
    }
  }

  &__content {
    width: 100%;

    .test-container {
      .add-test-area {
        .add-test-icon {
          color: var(--brand-primary) !important;
          font-size: $font-size-2xl;
          margin-right: $space-1;

          &-no-txt {
            margin-right: 0;
          }
        }

        .add-test-btn-txt {
          background-color: transparent;
          box-shadow: none;
          color: var(--brand-primary);
          font-family: $default-application-font-medium;
          font-size: rem(13);
          letter-spacing: 1.1px;
          line-height: $space-4;
          text-transform: uppercase;
        }
      }
    }

    .main-info {
      align-items: center;
      display: flex;

      .url {
        align-items: center;
        color: var(--text-primary);
        cursor: pointer;
        display: flex;
        width: 28%;
        font-family: $default-application-font-medium;
        font-size: $font-size-base;
        line-height: $space-5;
        height: $space-8;
        margin-right: 2.5%;
        flex-direction: row;
        justify-content: space-between;

        &__open {
          .open-cloud-btn {
            background-color: var(--btn-action-bg);
            text-align: center;
            box-shadow: none;
            min-width: rem(28);
            max-width: rem(28);
            min-height: rem(28);
            max-height: rem(28);
            user-select: none;
            color: var(--btn-action-border-color) !important;
            padding: 0;

            :deep(.v-icon) {
              font-size: $font-size-lg;
              color: var(--btn-action-border-color) !important;
            }

            &:hover {
              cursor: pointer;
              border: 1px solid var(--btn-action-border-color);
              background-color: var(--btn-action-hover-bg);
            }

            &:active {
              border: 1px solid var(--btn-action-border-color);
              background-color: var(--btn-action-hover-bg);
            }

            &::before {
              color: transparent;
            }
          }
        }

        &__data {
          width: 90%;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;

          &__tooltip {
            color: var(--text-primary);
            background-color: transparent;
          }
        }

        &__logo {
          max-width: rem(28);
          margin-top: $space-2;
          margin-right: $space-2;

          img {
            width: rem(28);
          }
        }
      }

      .ports {
        width: 23%;

        .port {
          z-index: 5;
          min-width: fit-content;
          max-width: fit-content;
          margin-bottom: $space-0-5;
          background: $issue-severity-low-background-color !important;
          color: var(--text-secondary);
          text-align: center;
          font-weight: 500;
          font-size: $font-size-base;
          margin-left: 2%;
          cursor: default;

          &.vulnerable {
            background: $issue-severity-critical-background-color !important;
            color: var(--severity-critical);
          }

          &__clickable {
            border: 1px solid;
            cursor: pointer;
          }
        }
      }

      .status {
        width: 12%;
        height: $space-5;
        font-family: $default-application-old-font-regular;
        letter-spacing: 0;
        line-height: $space-5;
        text-transform: capitalize;
        padding-left: 0.2%;

        .label {
          font-size: $font-size-base;
          color: var(--text-primary);

          &.colorBoolean {
            width: rem(54);
            color: var(--severity-critical);
            font-weight: 600;
          }
        }
      }

      .last-scanned-date {
        height: $space-5;
        width: 12%;
        color: var(--text-primary);
        font-family: $default-application-old-font-regular;
        font-size: $font-size-base;
        letter-spacing: 0;
        line-height: $space-5;
      }

      .cloud-account {
        height: $space-5;
        width: 14%;
        color: var(--text-primary);
        font-family: $default-application-old-font-regular;
        font-size: $font-size-base;
        letter-spacing: 0;
        line-height: $space-5;
      }

      .right-section {
        display: flex;
        width: 3%;
        position: absolute;
        right: 2.5%;

        .add-test-card {
          display: flex;
          justify-content: flex-end;
          flex: auto;
          flex-direction: row;
          width: rem(150);

          :deep(.v-btn:not(.v-btn--round).v-size--default) {
            background-color: var(--btn-action-bg);
            padding: $space-2;
            margin-left: $space-4;
          }

          .add-test-icon {
            color: var(--brand-primary);
            font-size: $font-size-2xl;
            margin-right: $space-1;

            &-no-txt {
              margin-right: 0;
            }
          }

          .add-test-btn {
            background-color: var(--btn-action-bg);
            text-align: center;
            box-shadow: none;
            min-width: rem(28);
            max-width: rem(28);
            min-height: rem(28);
            max-height: rem(28);
            user-select: none;
            color: var(--btn-action-border-color) !important;

            .v-icon {
              font-size: $font-size-lg;
              color: var(--btn-action-border-color) !important;
            }

            &:hover {
              cursor: pointer;
              border: 1px solid var(--btn-action-border-color);
              background-color: var(--btn-action-hover-bg);
            }

            &:active {
              border: 1px solid var(--btn-action-border-color);
              background-color: var(--btn-action-hover-bg);
            }

            &::before {
              color: transparent;
            }
          }
        }

        .arrow-expand-section {
          display: flex;
          justify-content: flex-end;
        }
      }
    }
  }
}
</style>
